import React from 'react';
import { Container, Accordion, Row, Col } from 'react-bootstrap';
import { AppleLogo, GooglePlayLogo } from 'phosphor-react';
import { Helmet } from 'react-helmet-async';

const APP_STORE_TRACKED = '/go/selfie';
const PLAY_STORE_TRACKED = '/play/selfie';

function SelfiePage() {
  const faqs = [
    {
      q: 'What is Selfie?',
      a: "Selfie\u2122 is the Family Ring service that builds a private AI memory of a family member after they've recorded a few videos. The family can ask Selfie questions and get answers based on what that person actually said in their stories."
    },
    {
      q: 'How does Selfie know what to say?',
      a: "Selfie is trained only on the videos that family member chose to record in Family Ring. It doesn't pull from the internet, social media, or anywhere outside your family ring. If your grandfather hasn't told a story about a particular topic, Selfie won't make one up."
    },
    {
      q: "Who can talk to a family member's Selfie?",
      a: "Only the people in that family ring. Selfie is private to the invited circle. It's never available outside the family ring it belongs to."
    },
    {
      q: 'Is Selfie text or voice?',
      a: "Today Selfie is text — your family asks questions and gets answers in writing, drawn from the recorded videos. Voice and video versions are possible in the future depending on what families ask for."
    },
    {
      q: 'How many videos before Selfie works?',
      a: "Selfie starts working once a family member has recorded around five videos. The more they record, the richer Selfie gets."
    },
    {
      q: "What happens to Selfie if a family member passes away?",
      a: "Selfie continues to be available to your family ring. The whole point is that the people you love and the stories they shared don't disappear when they're gone."
    }
  ];

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a }
    }))
  };

  return (
    <>
      <Helmet>
        <title>Selfie&trade; — Private AI Memory For Your Family | Family Ring</title>
        <meta
          name="description"
          content="Selfie is a private AI memory built from your family member's recorded stories. Ask questions, get answers in their own words. Private to your family ring. Part of Family Ring."
        />
        <link rel="canonical" href="https://familyring.io/selfie" />
        <meta property="og:title" content="Selfie — Private AI Memory For Your Family" />
        <meta
          property="og:description"
          content="A private AI memory built from your family member's actual recorded stories. Ask grandfather what it was like growing up, even when he's not around to answer."
        />
        <meta property="og:url" content="https://familyring.io/selfie" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <section className="py-5 compare-hero selfie-hero">
        <Container>
          <Row className="align-items-center gy-4">
            <Col lg={7}>
              <div className="selfie-icon-card">
                <img
                  src="/brand/selfie-icon.png"
                  alt="Selfie"
                  width="120"
                  height="120"
                />
              </div>
              <h1 className="display-5 mt-3 mb-3">
                Selfie&trade; — The people you love don't have to disappear when they're gone.
              </h1>
              <p className="lead text-muted mb-4">
                <strong>Selfie</strong> is our service that designs and develops a private AI
                memory of each member of your family ring — a custom AI trained on
                their own recorded stories. Once they've recorded a few videos, anyone in your
                ring can ask Selfie questions — about their childhood, their work,
                their family, what they believed — and get answers drawn from what
                they actually said in their stories.
              </p>
              <p className="lead text-muted mb-4">
                Today, Selfie is text. Voice and video are possible later if families
                want them. What's true today: it works after about five recorded
                videos, the more recordings the better, and it's only available to the
                family ring it belongs to. Selfie is available in the Family Ring app
                and on the web at familyring.io.
              </p>

              <div className="d-flex flex-wrap gap-3 mb-4">
                <a className="download-badge apple" href={APP_STORE_TRACKED}>
                  <AppleLogo size={36} weight="fill" />
                  <div className="download-badge-text">
                    <small>Download on the</small>
                    <span>App Store</span>
                  </div>
                </a>
                <a className="download-badge google" href={PLAY_STORE_TRACKED}>
                  <GooglePlayLogo size={36} weight="fill" />
                  <div className="download-badge-text">
                    <small>Get it on</small>
                    <span>Google Play</span>
                  </div>
                </a>
              </div>
              <p className="small text-muted">
                Free for 6 months &middot; Then $4.99/month for the whole family
              </p>
            </Col>
            <Col lg={5} className="text-center">
              <img
                src="/brand/selfie-forest.png"
                alt="Grandmother and granddaughter walking together in a forest"
                className="selfie-hero-image"
                width="568"
                height="791"
              />
            </Col>
          </Row>
        </Container>
      </section>

      <section className="py-5 wedge-section">
        <Container style={{ maxWidth: 820 }}>
          <h2 className="section-title mb-3">Selfie is yours.</h2>
          <p>
            Selfie uses only the videos a family member has chosen to record in
            Family Ring. It doesn't pull from the internet. It doesn't pull from
            social media. It isn't trained on anything outside your family ring.
            And it isn't available to anyone outside your family ring.
          </p>
          <p>
            If grandfather hasn't told a story about a topic, Selfie won't invent
            one. What you get back is grounded in what he actually said in his own
            recorded videos. The longer he uses Family Ring, the richer his Selfie
            becomes.
          </p>
        </Container>
      </section>

      <section className="py-5 wedge-section-alt">
        <Container style={{ maxWidth: 820 }}>
          <h2 className="section-title mb-3">Why this matters.</h2>
          <p>
            Most families have one or two relatives whose stories carry the family
            history. When they're gone, those stories usually go with them. A few
            photos remain. Maybe a Facebook tribute. The voicemail you couldn't
            bring yourself to delete.
          </p>
          <p>
            Selfie is a way to keep something more. Not a replacement for the
            person — nothing is — but a way for grandchildren who never met
            grandpa to ask him about the family business he started, the war he
            served in, the way he met grandma. To get an answer in his own words,
            because he recorded those words while he was here.
          </p>
          <p>
            That's the goal worth being honest about. Family Ring is fun and
            modern and easy to use day to day. Selfie is the thing your family
            will be glad you started today, ten and twenty years from now.
          </p>
        </Container>
      </section>

      <section className="py-5 faq-section">
        <Container style={{ maxWidth: 820 }}>
          <h2 className="section-title mb-4">Selfie questions families ask</h2>
          <Accordion>
            {faqs.map((item, i) => (
              <Accordion.Item eventKey={String(i)} key={item.q}>
                <Accordion.Header>{item.q}</Accordion.Header>
                <Accordion.Body>{item.a}</Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Container>
      </section>

      <section className="py-5 closing-cta-section">
        <Container className="text-center">
          <h2 className="mb-3">Start your Family Ring.</h2>
          <p className="text-muted mb-4">
            Six months free, full features. Your stories — and the people who told
            them — preserved in a way that's actually theirs.
          </p>
          <div className="d-flex flex-wrap gap-3 justify-content-center">
            <a className="download-badge apple" href={APP_STORE_TRACKED}>
              <AppleLogo size={36} weight="fill" />
              <div className="download-badge-text">
                <small>Download on the</small>
                <span>App Store</span>
              </div>
            </a>
            <a className="download-badge google" href={PLAY_STORE_TRACKED}>
              <GooglePlayLogo size={36} weight="fill" />
              <div className="download-badge-text">
                <small>Get it on</small>
                <span>Google Play</span>
              </div>
            </a>
          </div>
          <p className="text-muted mt-3 mb-0 small">
            6 months free &middot; Then $4.99/month for the whole family &middot; Cancel anytime
          </p>
        </Container>
      </section>
    </>
  );
}

export default SelfiePage;
